import React from 'react';

import {
  AccountInfoResult,
  DeployResultWithInfo,
  isContractTypeCep18,
} from '@block-explorer/entities';
import { FlexRow, TableData, TableRow } from '@make-software/cspr-design';
import {
  BodyText,
  formatNumber,
  formatTimestamp,
  formatTimestampAge,
  Tooltip,
} from '@block-explorer/ui';
import {
  DeployStatus,
  DeployStatusSize,
  isDeployAmountInCspr,
  TableDataAccountInfo,
  TableDataActionMessage,
  TableDataCurrencyAmount,
  TableDataEntryPoint,
} from '../../components';
import { BLOCK_DETAILS_PATH } from '../../router/paths';
import { buildRouterPath } from '../../router/utils';
import {
  deriveAccountInfo,
  deriveHashListByKeysFromDeployArgsObject,
} from '../../utils/account';
import { useAccountInfoContext } from '../../services/account-data-service';
import { TransactionHashLink } from '../hash-link/transaction-hash-link';
import { getAmountFromDeploy } from '../../utils/amount';
import Big from 'big.js';
import { HistoryLink } from '../history-link/history-link';
import { isODRAWasmProxyTransaction } from '../../utils/contract';
import { deriveFtTokenData } from '../../services';
import { useTranslation } from 'react-i18next';

interface DeployListItemProps {
  data: DeployResultWithInfo;
  loading: boolean;
  accountInfo?: AccountInfoResult | null | undefined;
  showBlockInfo?: boolean;
  showAccountInfo?: boolean;
  showOnlyEntryPointName?: boolean;
}

export const DeployListItem = ({
  data,
  loading,
  showBlockInfo = true,
  showAccountInfo = true,
  showOnlyEntryPointName = false,
}: DeployListItemProps) => {
  const { t } = useTranslation();
  const {
    callerPublicKey,
    callerHash,
    blockHash,
    timeTransactionCurrencyRate,
    deployHash,
    timestamp,
    contractPackage,
    ftActions,
    args,
    callerCsprName,
    paymentAmount,
    refundAmount,
    blockHeight,
  } = data;

  const amount = getAmountFromDeploy(args);
  const chargedAmount = Big(paymentAmount || '0')
    .minus(refundAmount || '0')
    .toString();

  const { getAccountInfo } = useAccountInfoContext();
  const accountData = getAccountInfo<AccountInfoResult>(
    callerPublicKey || callerHash
  );

  const combinedAccountInfo =
    accountData?.account_info || accountData?.centralized_account_info;
  const accountInfoDetails = deriveAccountInfo(combinedAccountInfo);

  const logo = accountInfoDetails && accountInfoDetails?.logo;
  const name = accountInfoDetails?.name;
  const csprName = accountData?.cspr_name;

  const tokenHashes = deriveHashListByKeysFromDeployArgsObject(
    [data],
    ['pinata_contract', 'token_contract']
  );

  const ftAction = ftActions?.[0];
  const ftActionContractPackage = ftAction?.contract_package;
  const isShibooToken = tokenHashes?.includes(config.shiboo_contract_hash);
  const isODRAWasm = isODRAWasmProxyTransaction(data);

  // TODO: Temporary created this flag, because ERC 20 token implementation cancels
  //  all the security benefits of special treatment of the amount field.
  //  Should be removed further.
  const isCep18 =
    isContractTypeCep18(contractPackage) ||
    isContractTypeCep18(ftActionContractPackage) ||
    isShibooToken;

  const hookProps = () => {
    if (isShibooToken) {
      return { contractPackageInfo: contractPackage, isShiboo: true };
    }
    if (ftActionContractPackage) {
      return { contractPackageInfo: ftActionContractPackage };
    }
    if (isCep18 && contractPackage) {
      return { contractPackageInfo: contractPackage };
    }

    return null;
  };

  const cep18AmountData = deriveFtTokenData(hookProps());

  const isCSPR = isDeployAmountInCspr(data);
  const isAstralBeamODRAWasm =
    isODRAWasm &&
    contractPackage?.contract_package_hash ===
      config.astral_beam_contract_package_hash &&
    isContractTypeCep18(ftActionContractPackage);
  const shouldShowCep18AmountData =
    isCep18 && (!isODRAWasm || isAstralBeamODRAWasm);
  const displayAmount = isAstralBeamODRAWasm ? ftAction?.amount : amount;

  const amountCurrencyRate = shouldShowCep18AmountData
    ? cep18AmountData?.price
    : isCSPR
      ? timeTransactionCurrencyRate
      : null;

  const amountData = shouldShowCep18AmountData ? cep18AmountData : null;

  return (
    <TableRow key={deployHash}>
      <TableData>
        <FlexRow align={'center'}>
          <DeployStatus deployResult={data} size={DeployStatusSize.Small} />
          <Tooltip tooltipContent={deployHash} caption={t('Transaction Hash')}>
            <BodyText size={3} monotype>
              <TransactionHashLink isDetails hash={deployHash} />
            </BodyText>
          </Tooltip>
        </FlexRow>
      </TableData>
      {showBlockInfo && (
        <TableData>
          <BodyText size={3} monotype>
            {blockHash == null || blockHeight == null ? (
              'N/A'
            ) : (
              <HistoryLink
                href={buildRouterPath(
                  BLOCK_DETAILS_PATH,
                  { id: blockHash },
                  undefined
                )}
                ariaDescription={'Link to block details'}
              >
                {formatNumber(blockHeight)}
              </HistoryLink>
            )}
          </BodyText>
        </TableData>
      )}
      <TableData>
        <Tooltip tooltipContent={formatTimestamp(timestamp)} monotype>
          <BodyText size={3} noWrap>
            {formatTimestampAge(timestamp)}
          </BodyText>
        </Tooltip>
      </TableData>
      {showAccountInfo && (
        <TableData>
          <TableDataAccountInfo
            logo={logo}
            name={name}
            publicKey={callerPublicKey || callerHash}
            csprName={callerCsprName || csprName}
            loading={loading}
            nameSize={1}
          />
        </TableData>
      )}
      <TableData>
        {showOnlyEntryPointName ? (
          <TableDataEntryPoint deployData={data} loading={loading} />
        ) : (
          <TableDataActionMessage deployData={data} loading={loading} />
        )}
      </TableData>
      <TableData align="right">
        <TableDataCurrencyAmount
          isCSPR={isCSPR}
          amount={displayAmount}
          cep18AmountData={amountData}
          tokenCurrencyRate={amountCurrencyRate ?? undefined}
          loading={loading}
          shouldShortenAmount
        />
      </TableData>
      <TableData align="right">
        <TableDataCurrencyAmount
          amount={chargedAmount}
          tokenCurrencyRate={timeTransactionCurrencyRate}
          loading={loading}
        />
      </TableData>
    </TableRow>
  );
};
